import { VariantProps } from "class-variance-authority";
import { ChevronUp, Video, VideoOff } from "lucide-react";
import { forwardRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useShallow } from "zustand/react/shallow";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem } from "@/components/ui/select";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import * as SelectPrimitive from "@radix-ui/react-select";

import { detectChromium, getShortcutKey } from "../../utils/hotkey-helper";
import { ActionButton, ActionButtonProps } from "../action-button";

function SelectCamera() {
  const vidyoSDK = useVidyoStore((state) => state.vidyoSDK);

  const cameras = useVidyoStore((state) => state.camera.available);
  const selectedCamera = useVidyoStore(
    useShallow((state) => ({
      id: state.camera.selected?.id,
      name: state.camera.selected?.name,
    })),
  );
  const setPreferredCamera = useVidyoStore(
    (state) => state.camera.setPreferred,
  );
  const handleSelectCamera = async (cameraId: string) => {
    const camera = cameras.find((c) => c.id === cameraId);
    if (camera) {
      await vidyoSDK?.SelectLocalCamera({
        localCamera: camera,
      });
      setPreferredCamera(camera);
    }
  };

  return (
    <Select value={selectedCamera?.id} onValueChange={handleSelectCamera}>
      <SelectPrimitive.Trigger disabled={cameras.length === 0} asChild>
        <Button variant="ghost" size="xs" className="p-1">
          <ChevronUp className="size-4" />
        </Button>
      </SelectPrimitive.Trigger>
      <SelectContent>
        {cameras.map((camera) => (
          <SelectItem key={camera.id} value={camera.id}>
            {camera.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type ToggleCameraProps = Omit<ActionButtonProps, "Icon" | "text"> &
  VariantProps<typeof ActionButton> & {
    size?: "fixed" | "auto";
  };

export const ToggleCamera = forwardRef<HTMLButtonElement, ToggleCameraProps>(
  ({ size = "fixed", ...props }, ref) => {
    const isCameraMuted = useVidyoStore((state) => state.camera.shouldBeMuted);
    const isVidyoConnected = useVidyoStore((state) => state.isVidyoConnected);
    const toggleCamera = useVidyoStore((state) => state.camera.toggle);
    const isCameraServerMuted = useVidyoStore(
      (state) => state.camera.serverMuted,
    );
    useHotkeys("mod+e", () => toggleCamera(), [toggleCamera], {
      preventDefault: true,
    });

    return (
      <ActionButton
        {...props}
        ref={ref}
        size={size}
        onClick={toggleCamera}
        Icon={isCameraMuted ? VideoOff : Video}
        key="camera"
        tooltipText={
          detectChromium() ? (
            isCameraMuted ? (
              <span className="text-muted">{getShortcutKey()} + E</span>
            ) : (
              <span className="text-muted">{getShortcutKey()} + E</span>
            )
          ) : (
            ""
          )
        }
        text={isCameraMuted ? "Turn on" : "Turn off"}
        disabled={isCameraServerMuted || !isVidyoConnected}
        variant={isCameraMuted ? "destructive" : "default"}
        secondaryAction={<SelectCamera />}
      />
    );
  },
);

ToggleCamera.displayName = "ToggleCamera";
